/**
 * What the app is allowed to say, and how each phrase is worded.
 *
 * The server owns the wording. The client asks for a named use case with validated variables,
 * never free text, so the endpoint cannot be turned into somebody else's synthesiser — and so
 * the vocabulary stays small enough to precompute.
 *
 * Delivery is carried entirely by the words: Aura 2 exposes no pitch or rate through Cloudflare,
 * and responds to punctuation instead. A full stop buys the pause a hyphen used to, without the
 * clipped reading it gave; an exclamation mark lifts the line that follows.
 */

/** Only the two boarding stations with a drive to make. The ride home is silent. */
export const SPEECH_STATIONS: Record<string, string> = {
	CIVC: "Civic Center",
	GLEN: "Glen Park",
};

export type SpeechGreeting = "morning" | "afternoon" | "evening";

export const SPEECH_GREETINGS: SpeechGreeting[] = ["morning", "afternoon", "evening"];

export type SpeechVoice = "british" | "us";

/** Narrowed to what the binding accepts, so a typo cannot reach the model as a speaker name. */
export type SpeechSpeaker = "draco" | "atlas";

/**
 * The two voices worth offering, out of Aura 2's forty.
 *
 * Both are warm masculine reads at a similar pace, so the choice is accent and nothing else.
 * Keeping it to two is what makes precomputing every phrase in every voice affordable.
 */
export const SPEECH_VOICE_CHOICES: Record<SpeechVoice, { speaker: SpeechSpeaker; label: string }> = {
	british: { speaker: "draco", label: "British voice" },
	us: { speaker: "atlas", label: "US voice" },
};

export const SPEECH_VOICE_ORDER: SpeechVoice[] = ["british", "us"];

export const DEFAULT_SPEECH_VOICE: SpeechVoice = "british";

export function readSpeechVoice(value: unknown): SpeechVoice {
	return value === "us" || value === "british" ? value : DEFAULT_SPEECH_VOICE;
}

/** The lead times the alerts sheet offers: 0 to 60 in steps of five. */
export const SPEECH_LEADS: number[] = Array.from({ length: 13 }, (_, index) => index * 5);

export type SpeechUseCase = "lead" | "hurry" | "test" | "pickup";

/** Which moment of a pickup is being spoken. */
export type SpeechCue = "offer" | "leave";

export interface SpeechVariant {
	useCase: SpeechUseCase;
	/** Minutes before the leave deadline. Only meaningful for `lead`. */
	minutesBefore?: number;
	/** BART id of the boarding station. Absent for `test`. */
	station?: string;
	/** Absent where the phrase carries no greeting. */
	greeting?: SpeechGreeting;
	/** Which of the test lines. Only meaningful for `test`; the worker picks when it is absent. */
	index?: number;
	/** Only meaningful for `pickup`. */
	cue?: SpeechCue;
}

/**
 * What a test reminder says.
 *
 * A fixed set rather than something written on the spot: every one of these renders once and is
 * then cached for good, so the button answers instantly instead of waiting on a model, and the
 * words are ones somebody chose rather than ones a machine thought were funny.
 */
export const TEST_LINES: string[] = [
	"Spoken reminders are working.",
	"This is a test. Everything sounds fine.",
	"Sound check. Reminders will arrive in this voice.",
	"If you can hear this, your reminders will reach you.",
	"Volume check. This is how a reminder will sound.",
	"Just a test. No train is involved.",
	"This is what a leave time reminder sounds like.",
	"Testing the speaker. Nothing is due.",
	"Reminders are switched on and working.",
	"You are hearing the reminder voice.",
	"All set. Nothing to do.",
	"A quick check, nothing more.",
	"This is the voice that will tell you when to leave.",
	"Sound is working. Back to your day.",
	"Test message. Your alerts are ready.",
	"Reminders will sound like this, at the times you chose.",
	"Speaker test passed.",
	"Everything is working as it should.",
	"Loud enough? Adjust the volume and try again.",
	"That is all. This was only a test.",
];

/** Spoken rather than written, because "fifteen" is never misread and "15" occasionally is. */
const SPOKEN_NUMBERS: Record<number, string> = {
	5: "five", 10: "ten", 15: "fifteen", 20: "twenty", 25: "twenty-five", 30: "thirty",
	35: "thirty-five", 40: "forty", 45: "forty-five", 50: "fifty", 55: "fifty-five", 60: "sixty",
};

function spoken(minutes: number): string {
	return SPOKEN_NUMBERS[minutes] ?? String(minutes);
}

/**
 * The greeting only ever opens a reminder with time left to act on it.
 *
 * Three "good morning"s inside a quarter of an hour grates, and once the deadline is close a
 * pleasantry is in the way of the instruction.
 */
export function greetingText(greeting: SpeechGreeting): string {
	return `Good ${greeting}!`;
}

/** Below this the reminder is an instruction, not an announcement, so it opens differently. */
const GREETED_ABOVE_MINUTES = 5;

/**
 * How a phrase is named in the client's clip cache.
 *
 * Every field that changes the words has to be in here. `cue` was missing, so the two pickup
 * lines shared one entry: the offer clip took the slot, the leave clip was never fetched because
 * the cache already claimed to hold it, and the leave moment either said nothing or would have
 * said the offer's words. Kept beside the catalogue so a new field cannot be added in one place
 * and forgotten in the other.
 */
export function speechVariantKey(variant: SpeechVariant, voice: SpeechVoice): string {
	return [
		voice,
		variant.useCase,
		variant.minutesBefore ?? "",
		variant.station ?? "",
		variant.greeting ?? "",
		variant.cue ?? "",
	].join(":");
}

/** The phrase for one variant, or null when the variant does not describe a real reminder. */
export function speechText(variant: SpeechVariant): string | null {
	if (variant.useCase === "test") {
		return TEST_LINES[variant.index ?? 0] ?? null;
	}
	const station = variant.station == null ? null : SPEECH_STATIONS[variant.station];
	if (!station) {
		return null;
	}
	/*
	 * The pickup lines.
	 *
	 * The one that matters is `offer`: David expects to have the app open beside his computer,
	 * so the voice is what actually reaches him, and a push alone would not. `leave` is the single
	 * nudge afterwards — there is no ladder, because by then he is already waiting to go.
	 */
	if (variant.useCase === "pickup") {
		if (variant.cue === "leave") {
			return `Time to leave for ${station}. Eben is on his way.`;
		}
		return variant.cue === "offer" ? `Eben is heading home. Pick up at ${station}.` : null;
	}
	if (variant.useCase === "hurry") {
		return `Leave now for ${station}. You'll need to be quick!`;
	}

	const minutes = variant.minutesBefore ?? 0;
	if (!SPEECH_LEADS.includes(minutes)) {
		return null;
	}
	if (minutes === 0) {
		return `Leave now for ${station}. Off you go!`;
	}
	if (minutes <= GREETED_ABOVE_MINUTES) {
		return `Grab your things! ${capitalise(spoken(minutes))} minutes until you leave for ${station}.`;
	}
	// "About", because the card shows this reminder as a range once prep time is counted, and
	// what he hears must not disagree with what he reads.
	const opener = variant.greeting ? `${greetingText(variant.greeting)} ` : "";
	return `${opener}You're leaving for ${station} in about ${spoken(minutes)} minutes. Time to get ready!`;
}

function capitalise(value: string): string {
	return `${value[0].toUpperCase()}${value.slice(1)}`;
}

/**
 * Every phrase that could actually be spoken.
 *
 * `reachableOnly` drops the greetings that cannot occur: the greeting comes from the reminder's
 * own clock, and a morning arrival target puts every reminder before noon. Generating the other
 * two thirds would spend most of a day's free allowance on audio nothing can play.
 *
 * The test lines are in here too, so pressing the button plays something already rendered rather
 * than waiting on a synthesis.
 */
export function speechCatalogue(reachableOnly = true): SpeechVariant[] {
	const greetings = reachableOnly ? (["morning"] as SpeechGreeting[]) : SPEECH_GREETINGS;
	const stations = Object.keys(SPEECH_STATIONS);
	const variants: SpeechVariant[] = TEST_LINES.map((_line, index) => ({ useCase: "test", index }) satisfies SpeechVariant);
	for (const station of stations) {
		variants.push({ useCase: "hurry", station });
		variants.push({ useCase: "pickup", cue: "offer", station });
		variants.push({ useCase: "pickup", cue: "leave", station });
		for (const minutesBefore of SPEECH_LEADS) {
			// A short lead has no greeted form, so asking for one would only duplicate a clip.
			if (minutesBefore > GREETED_ABOVE_MINUTES) {
				for (const greeting of greetings) {
					variants.push({ useCase: "lead", minutesBefore, station, greeting });
				}
			}
			// The ungreeted form, used for every reminder after the first of the morning.
			variants.push({ useCase: "lead", minutesBefore, station });
		}
	}
	return variants;
}

/** Rejects before anything is spent, so a bad request costs nothing. */
export function readSpeechVariant(body: unknown): SpeechVariant | { error: string } {
	const raw = body as { useCase?: unknown; minutesBefore?: unknown; station?: unknown; greeting?: unknown; index?: unknown; cue?: unknown } | null;
	const useCase = raw?.useCase;
	if (useCase !== "lead" && useCase !== "hurry" && useCase !== "test" && useCase !== "pickup") {
		return { error: "Unknown use case." };
	}
	const variant: SpeechVariant = { useCase };
	if (raw?.cue === "offer" || raw?.cue === "leave") {
		variant.cue = raw.cue;
	}
	if (typeof raw?.index === "number" && Number.isInteger(raw.index) && raw.index >= 0 && raw.index < TEST_LINES.length) {
		variant.index = raw.index;
	}
	if (typeof raw?.station === "string") {
		variant.station = raw.station;
	}
	if (typeof raw?.minutesBefore === "number") {
		variant.minutesBefore = raw.minutesBefore;
	}
	if (raw?.greeting === "morning" || raw?.greeting === "afternoon" || raw?.greeting === "evening") {
		variant.greeting = raw.greeting;
	}
	return speechText(variant) == null ? { error: "That is not a phrase GoTime says." } : variant;
}
